import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import type { ProductCard, PricingQuoteResponse } from "@itaas/pep-contracts";
import { getPricingQuote, getProduct } from "../api/pep-api";
import { ProductCardView } from "../components/catalog/ProductCard";
import { SiteLayout } from "../components/layout/SiteLayout";
import { productJsonLd, SeoHead } from "../components/seo/SeoHead";
import { PublisherTrustStrip } from "../components/trust/InstitutionalTrust";
import { usePepI18n } from "../i18n/usePepI18n";
import { SUITE_URL } from "../config";
import {
  editionLabel,
  labelCategory,
  labelIndustry,
  labelStatus,
  productDescription,
  productDisplayName,
  productTagline,
  statusBadgeClass,
} from "../utils/labels";
import { isPrelaunchProduct } from "../utils/product-status";
import { hasRichProductPresentation } from "../utils/product-presentation";
import { ProductLogo } from "../components/catalog/ProductLogo";
import { ProductContentSection } from "../components/product/ProductContentSection";
import { ProductPromotionBadge } from "../components/commercial/ProductPromotionBadge";
import { PromoPrice } from "../components/commercial/PromoPrice";
import { isPromotionActive } from "../utils/product-promotion";
import { ProductStatsBar } from "../components/product/ProductStatsBar";
import { ProductHighlightGrid } from "../components/product/ProductHighlightGrid";
import { ProductFeatureGroups } from "../components/product/ProductFeatureGroups";
import { ProductEditionShowcase } from "../components/product/ProductEditionShowcase";
import {
  ProductPersonaCards,
  ProductVerticalCrossLinks,
  ProductVerticalHeroExtras,
} from "../components/product/ProductVerticalExtras";
import { PRODUCTS_PUBLISHABLE_SLUGS, commercialProductName } from "../products/products-content";
import { AutoidProductDetailHd } from "../products/detail/AutoidProductDetailHd";
import { CrmProductDetailHd } from "../products/detail/CrmProductDetailHd";
import { EduProductDetailHd } from "../products/detail/EduProductDetailHd";
import { ErpProductDetailHd } from "../products/detail/ErpProductDetailHd";
import { HospitolProductDetailHd } from "../products/detail/HospitolProductDetailHd";
import { InventoryProductDetailHd } from "../products/detail/InventoryProductDetailHd";
import { InvoiceProductDetailHd } from "../products/detail/InvoiceProductDetailHd";
import { MineportalProductDetailHd } from "../products/detail/MineportalProductDetailHd";
import { RhProductDetailHd } from "../products/detail/RhProductDetailHd";
import { SecurityProductDetailHd } from "../products/detail/SecurityProductDetailHd";
import {
  buildConfigureHrefForProduct,
  commercialCtaLabelForProduct,
} from "../utils/commercial-cta";

const PRODUCT_DETAIL_HD: Record<
  string,
  | typeof EduProductDetailHd
  | typeof RhProductDetailHd
  | typeof InvoiceProductDetailHd
  | typeof CrmProductDetailHd
  | typeof InventoryProductDetailHd
  | typeof MineportalProductDetailHd
  | typeof SecurityProductDetailHd
  | typeof ErpProductDetailHd
  | typeof AutoidProductDetailHd
  | typeof HospitolProductDetailHd
> = {
  edu: EduProductDetailHd,
  rh: RhProductDetailHd,
  billing: InvoiceProductDetailHd,
  crm: CrmProductDetailHd,
  inventory: InventoryProductDetailHd,
  mineportal: MineportalProductDetailHd,
  security: SecurityProductDetailHd,
  erp: ErpProductDetailHd,
  autoid: AutoidProductDetailHd,
  hospitol: HospitolProductDetailHd,
};

/** Public detail must not promote HIDDEN / CAPABILITY modules as related products. */
const BLOCKED_PUBLIC_CLAIM =
  /\b(comex|crm|multi-tenant|modularit[eé]\s+itaas|conformit[eé]\s+minedu|minedu(?:-nc)?)\b/i;

function scrubPublicText(
  text: string,
  locale: "fr" | "en",
  options?: { productSlug?: string },
): string {
  let out = text
    .replace(/\bMINEDU(?:-NC)?\b/gi, locale === "en" ? "DRC school standards" : "référentiel scolaire RDC")
    .replace(/\bmulti-tenant\b/gi, locale === "en" ? "multi-organization" : "multi-organisation")
    .replace(/\bComex\b/gi, locale === "en" ? "inbound logistics" : "flux inbound");
  // Do not scrub the product's own identity on its detail page.
  if (options?.productSlug !== "crm") {
    out = out.replace(/\bCRM\b/g, locale === "en" ? "commercial pipeline" : "pipeline commercial");
  }
  return out;
}

function publicFeatureLines(
  lines: string[] | undefined,
  options?: { productSlug?: string },
): string[] {
  return (lines ?? [])
    .filter((f) => {
      if (!f) return false;
      if (options?.productSlug === "crm") return true;
      return !BLOCKED_PUBLIC_CLAIM.test(f);
    })
    .map((f) => scrubPublicText(f, "fr", options));
}

function safeAvailabilityNote(product: ProductCard): string | undefined {
  const note = product.availabilityNote?.trim();
  if (!note) return undefined;
  const trialBlocked =
    product.portfolio?.trialAllowed === false &&
    /essai|trial|30\s*j/i.test(note);
  if (trialBlocked) {
    return "Parcours de démonstration — périmètre et maturité à confirmer avec ITaaS RDC.";
  }
  return note;
}

function ctaAllowed(
  product: ProductCard,
  cta: "DEMO" | "QUOTE" | "TRIAL" | "CONTACT" | "LEARN" | "PURCHASE",
): boolean {
  const allowed = product.portfolio?.allowedCtas;
  if (!allowed || allowed.includes("NONE")) {
    if (cta === "LEARN") return true;
    if (!allowed) {
      // Legacy cards without portfolio: keep previous heuristics
      return true;
    }
    return false;
  }
  return allowed.includes(cta);
}

export function ProductDetailPage() {
  const { slug = "" } = useParams();
  const { locale, t } = usePepI18n();
  const [product, setProduct] = useState<(ProductCard & { complementary?: ProductCard[] }) | null>(
    null,
  );
  const [prices, setPrices] = useState<Record<string, PricingQuoteResponse>>({});
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setNotFound(false);
    getProduct(slug)
      .then(async (p) => {
        const redirectTo = p.portfolio?.redirectTo;
        const pageAllowed = p.portfolio?.publicPageAllowed ?? true;
        if (redirectTo && !pageAllowed) {
          setProduct(p);
          return;
        }
        if (slug === "suite" && redirectTo === "/") {
          setProduct(p);
          return;
        }
        setProduct(p);
        if (p.portfolio?.pricingAllowed === false) {
          setPrices({});
          return;
        }
        const editions = p.editions?.length
          ? p.editions
          : ["starter", "professional", "enterprise"];
        const quotes = await Promise.all(
          editions.map((e) =>
            getPricingQuote(slug, e, "USD").then((q) => [e, q] as const),
          ),
        );
        setPrices(Object.fromEntries(quotes));
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return <Navigate to="/products" replace />;
  }

  if (!product) {
    return (
      <SiteLayout>
        <div className="container py-5 text-secondary">{t("common.loading")}</div>
      </SiteLayout>
    );
  }

  const redirectTo = product.portfolio?.redirectTo;
  const pageAllowed = product.portfolio?.publicPageAllowed ?? true;
  if (redirectTo && !pageAllowed) {
    return <Navigate to={redirectTo} replace />;
  }
  if (slug === "suite" && redirectTo === "/") {
    return <Navigate to="/" replace />;
  }

  const name = productDisplayName(product, locale);
  const scrubOpts = { productSlug: product.slug };
  const rawDesc = productDescription(product, locale);
  const desc = scrubPublicText(rawDesc, locale, scrubOpts);
  const publicUseCases = (product.useCases ?? []).map((uc) =>
    scrubPublicText(uc, locale, scrubOpts),
  );
  const publicStats = (product.stats ?? []).map((s) => ({
    ...s,
    value: scrubPublicText(String(s.value ?? ""), locale, scrubOpts),
    labelFr: scrubPublicText(s.labelFr ?? "", "fr", scrubOpts),
    labelEn: scrubPublicText(s.labelEn ?? "", "en", scrubOpts),
  }));
  const publicRoadmap = publicFeatureLines(product.roadmapFeatures, scrubOpts).map((f) =>
    scrubPublicText(f, locale, scrubOpts),
  );
  const prelaunch = isPrelaunchProduct(product.status);
  const verticalRich = hasRichProductPresentation(product);
  const showTrial =
    Boolean(product.trialDays) &&
    product.portfolio?.trialAllowed !== false &&
    ctaAllowed(product, "TRIAL") &&
    !prelaunch;
  const availabilityNote = safeAvailabilityNote(product);
  const publicFeatures = publicFeatureLines(product.features, scrubOpts);
  const publicIntegrations = product.integrations.filter((slug) => PRODUCTS_PUBLISHABLE_SLUGS.has(slug));
  const publicHighlights = (product.highlights ?? []).filter((h) => {
    const blob = `${h.titleFr} ${h.titleEn ?? ""} ${h.descriptionFr} ${h.descriptionEn ?? ""}`;
    if (product.slug === "crm") return true;
    return !BLOCKED_PUBLIC_CLAIM.test(blob);
  });
  const publicFeatureGroups = (product.featureGroups ?? [])
    .map((g) => ({
      ...g,
      features: publicFeatureLines(g.features, scrubOpts),
    }))
    .filter((g) => g.features.length > 0);
  const showDemo = ctaAllowed(product, "DEMO");
  const showQuote = ctaAllowed(product, "QUOTE");
  const showPricing =
    product.portfolio?.pricingAllowed !== false &&
    product.portfolio?.pricingPolicy !== "NO_PRICE" &&
    product.portfolio?.pricingPolicy !== "NOT_APPLICABLE";
  const noindex = product.portfolio?.indexable === false;

  const displayProduct: ProductCard = {
    ...product,
    description: desc,
    descriptionFr: scrubPublicText(product.descriptionFr ?? product.description, "fr", scrubOpts),
    descriptionEn: scrubPublicText(product.descriptionEn ?? product.description, "en", scrubOpts),
    heroKickerFr: product.heroKickerFr
      ? scrubPublicText(product.heroKickerFr, "fr", scrubOpts)
      : product.heroKickerFr,
    heroKickerEn: product.heroKickerEn
      ? scrubPublicText(product.heroKickerEn, "en", scrubOpts)
      : product.heroKickerEn,
    useCases: publicUseCases,
    stats: publicStats,
    features: publicFeatures,
    highlights: publicHighlights,
    featureGroups: publicFeatureGroups,
    integrations: publicIntegrations,
    roadmapFeatures: publicRoadmap,
    availabilityNote,
  };

  const HdPage = PRODUCT_DETAIL_HD[product.slug];
  if (HdPage) {
    return (
      <SiteLayout>
        <SeoHead
          title={name}
          description={desc.slice(0, 160)}
          keywords={product.seoKeywords}
          canonicalPath={`/products/${product.slug}`}
          locale={locale}
          noIndex={noindex}
          jsonLd={productJsonLd({
            displayName: name,
            description: desc,
            slug: product.slug,
            status: product.status,
            keywords: product.seoKeywords,
          })}
          ogType="product"
        />
        <HdPage
          locale={locale}
          product={{ ...displayProduct, complementary: product.complementary }}
          prices={prices}
          description={desc}
          showDemo={showDemo}
          showTrial={showTrial}
        />
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <SeoHead
        title={name}
        description={desc.slice(0, 160)}
        keywords={product.seoKeywords}
        canonicalPath={`/products/${product.slug}`}
        locale={locale}
        noIndex={noindex}
        ogType="product"
        jsonLd={productJsonLd({
          displayName: name,
          description: desc,
          slug: product.slug,
          status: product.status,
          keywords: product.seoKeywords,
        })}
      />

      <div className={`pep-page-hero py-4 mb-0${verticalRich ? " pep-product-hero--vertical" : ""}`}>
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-3 pep-breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/products">Produits</Link>
              </li>
              <li className="breadcrumb-item active">{name}</li>
            </ol>
          </nav>
          <div className="row align-items-center">
            <div className="col-lg-8">
              {verticalRich ? <ProductVerticalHeroExtras product={displayProduct} locale={locale} /> : null}
              <div className="d-flex flex-wrap gap-2 mb-2">
                <span className={`badge ${verticalRich ? "pep-hero-badge" : "text-bg-light border"}`}>
                  {labelCategory(product.category, locale)}
                </span>
                <span className={`badge ${verticalRich ? "pep-hero-badge" : statusBadgeClass(product.status)}`}>
                  {labelStatus(product.status, locale)}
                </span>
                {isPromotionActive(product.promotion) ? (
                  <ProductPromotionBadge promotion={product.promotion} locale={locale} />
                ) : null}
              </div>
              <h1 className="display-5 fw-bold mb-2">{name}</h1>
              <p className="lead mb-0 opacity-90">{productTagline(product, locale)}</p>
              {verticalRich && publicStats.length ? (
                <ProductStatsBar stats={publicStats} locale={locale} />
              ) : null}
              {verticalRich && showTrial ? (
                <div className="d-flex flex-wrap gap-2 mt-4">
                  <Link className="btn btn-light btn-lg" to={buildConfigureHrefForProduct(product)}>
                    {commercialCtaLabelForProduct(product, locale)}
                  </Link>
                  {showDemo ? (
                    <Link
                      className="btn btn-outline-light btn-lg"
                      to={`/contact?product=${product.slug}&type=demo`}
                    >
                      {locale === "en" ? "Request a demo" : "Demander une démo"}
                    </Link>
                  ) : null}
                </div>
              ) : verticalRich && showDemo ? (
                <div className="d-flex flex-wrap gap-2 mt-4">
                  {showPricing ? (
                    <Link className="btn btn-light btn-lg" to={buildConfigureHrefForProduct(product)}>
                      {commercialCtaLabelForProduct(product, locale)}
                    </Link>
                  ) : null}
                  <Link
                    className={`btn ${showPricing ? "btn-outline-light" : "btn-light"} btn-lg`}
                    to={`/contact?product=${product.slug}&type=demo`}
                  >
                    {locale === "en" ? "Request a demo" : "Demander une démo"}
                  </Link>
                </div>
              ) : null}
            </div>
            <div className="col-lg-4 text-lg-end mt-4 mt-lg-0">
              <ProductLogo slug={product.slug} name={name} size={96} className="pep-product-logo-hero" />
            </div>
          </div>
        </div>
      </div>

      <div className="container py-5">
        {availabilityNote && (
          <div className={`alert border-0 shadow-sm mb-4 ${prelaunch ? "alert-warning" : "alert-info"}`}>
            <strong>{prelaunch ? (locale === "en" ? "In development — " : "En développement — ") : "Disponibilité —"}</strong>
            {availabilityNote}
            {prelaunch && (
              <>
                {" "}
                <Link to={`/contact?product=${product.slug}&type=info`} className="alert-link">
                  {locale === "en" ? "Contact sales →" : "Contact commercial →"}
                </Link>
              </>
            )}
          </div>
        )}

        <div className="row g-5">
          <div className="col-lg-8">
            <section className="mb-5">
              <h2 className="h4 fw-bold mb-3">
                {locale === "en" ? "Overview" : "Présentation"}
              </h2>
              <p className={`text-secondary ${verticalRich ? "lead fs-6" : ""}`}>{desc}</p>
              {!verticalRich &&
              (product.slug === "rh" ||
                product.slug === "edu" ||
                product.slug === "biometric-gateway") ? (
                <div className="alert alert-light border mt-3 mb-0 small">
                  <strong>Lecteur biométrique :</strong>{" "}
                  {product.slug === "rh"
                    ? "1 lecteur ZKTeco inclus par défaut avec ITaaS RH. Des lecteurs supplémentaires peuvent être commandés pour couvrir plusieurs sites ou entrées."
                    : product.slug === "edu"
                      ? "1 lecteur fourni par défaut pour le pointage élèves/personnel. Nous recommandons 1 lecteur par salle de classe ; la quantité est ajustable lors de l'essai ou du devis."
                      : "Passerelle de supervision des terminaux ZKTeco — la quantité de lecteurs est paramétrable selon vos sites et salles."}
                </div>
              ) : null}
              {verticalRich && product.slug === "edu" ? (
                <div className="alert alert-light border mt-3 mb-0 small">
                  <strong>Lecteur biométrique (option) :</strong> synchronisation présence élèves
                  via le module Attendance — 1 lecteur par défaut, extensible par salle de classe.
                </div>
              ) : null}
            </section>

            {verticalRich ? (
              <>
                {publicHighlights.length ? (
                  <ProductHighlightGrid highlights={publicHighlights} locale={locale} />
                ) : null}
                <ProductEditionShowcase product={displayProduct} prices={prices} locale={locale} />
                {publicFeatureGroups.length ? (
                  <ProductFeatureGroups groups={publicFeatureGroups} locale={locale} />
                ) : null}
              </>
            ) : null}

            <ProductContentSection productSlug={product.slug} />

            {!verticalRich && product.valuePropositions && product.valuePropositions.length > 0 && (
              <section className="mb-5">
                <h2 className="h4 fw-bold mb-3">Pourquoi {name} ?</h2>
                <div className="row g-3">
                  {product.valuePropositions.map((vp) => (
                    <div className="col-md-6" key={vp}>
                      <div className="p-3 rounded-3 bg-body-tertiary h-100">
                        <span className="text-primary fw-bold me-2">→</span>
                        {vp}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {!verticalRich && publicFeatures.length > 0 && (
              <section className="mb-5">
                <h2 className="h4 fw-bold mb-3">Fonctionnalités</h2>
                <ul className="row list-unstyled g-2">
                  {publicFeatures.map((f) => (
                    <li className="col-md-6 d-flex" key={f}>
                      <span className="text-success me-2 flex-shrink-0">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {verticalRich && publicFeatures.length > 0 && (
              <section className="mb-5">
                <h2 className="h4 fw-bold mb-3">
                  {locale === "en" ? "Key highlights" : "Points clés"}
                </h2>
                <ul className="row list-unstyled g-2">
                  {publicFeatures.map((f) => (
                    <li className="col-md-6 d-flex" key={f}>
                      <span className="text-success me-2 flex-shrink-0">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {publicRoadmap.length > 0 && (
              <section className="mb-5">
                <h2 className="h4 fw-bold mb-3">Prochainement</h2>
                <p className="text-secondary small mb-3">
                  Fonctionnalités en développement — transparence sur notre feuille de route.
                </p>
                <ul className="list-unstyled">
                  {publicRoadmap.map((f) => (
                    <li className="text-secondary mb-1" key={f}>
                      <span className="badge text-bg-light border me-2">Bientôt</span>
                      {f}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {publicUseCases.length > 0 ? (
              verticalRich ? (
                <ProductPersonaCards useCases={publicUseCases} locale={locale} />
              ) : (
                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">Cas d&apos;usage</h2>
                  <div className="row g-3">
                    {publicUseCases.map((uc) => (
                      <div className="col-md-6" key={uc}>
                        <div className="card border-0 shadow-sm h-100">
                          <div className="card-body">
                            <span className="fs-4">📌</span>
                            <p className="mb-0 mt-2">{uc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )
            ) : null}

            {product.industries.length > 0 && (
              <section className="mb-5">
                <h2 className="h4 fw-bold mb-3">Secteurs concernés</h2>
                <div className="d-flex flex-wrap gap-2">
                  {product.industries.map((ind) => (
                    <span className="badge text-bg-light border text-dark" key={ind}>
                      {labelIndustry(ind)}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {publicIntegrations.length > 0 && (
              <section className="mb-5">
                <h2 className="h4 fw-bold mb-3">S&apos;intègre avec</h2>
                <div className="d-flex flex-wrap gap-2">
                  {publicIntegrations.map((int) => (
                    <Link
                      key={int}
                      className="badge text-bg-primary text-decoration-none"
                      to={`/products/${int}`}
                    >
                      {commercialProductName(int, locale) ?? int}
                    </Link>
                  ))}
                </div>
                <p className="mt-3 mb-0">
                  <Link to={`/marketplace?product=${product.slug}`}>
                    Extensions & connecteurs compatibles →
                  </Link>
                </p>
              </section>
            )}

            {verticalRich ? (
              <ProductVerticalCrossLinks product={product} locale={locale} />
            ) : null}
          </div>

          <div className="col-lg-4">
            <div className="card border-0 shadow pep-cta-card sticky-top" style={{ top: "5rem" }}>
              <div className="card-body">
                {prelaunch ? (
                  <>
                    <h3 className="h6 text-secondary text-uppercase mb-2">
                      {locale === "en" ? "Coming soon" : "Bientôt disponible"}
                    </h3>
                    <p className="small text-secondary mb-3">
                      {locale === "en"
                        ? "This module is in active development. Pre-register to be notified at launch or for a private preview."
                        : "Ce module est en cours de développement. Préinscrivez-vous pour être informé au lancement ou pour un accès anticipé."}
                    </p>
                    <Link
                      className="btn btn-primary w-100 mb-2"
                      to={`/contact?product=${product.slug}&type=info`}
                    >
                      {locale === "en" ? "Contact sales" : "Contact commercial"}
                    </Link>
                    <Link
                      className="btn btn-outline-secondary w-100"
                      to={`/preregister?product=${product.slug}`}
                    >
                      {locale === "en" ? "Legacy waitlist form" : "Formulaire liste d’attente (legacy)"}
                    </Link>
                  </>
                ) : (
                  <>
                {showPricing ? (
                  <>
                <h3 className="h6 text-secondary text-uppercase mb-3">
                  Tarification indicative
                  {isPromotionActive(product.promotion) ? (
                    <span className="ms-2">
                      <ProductPromotionBadge promotion={product.promotion} locale={locale} />
                    </span>
                  ) : null}
                </h3>
                {product.editions.map((ed) => {
                  const quote = prices[ed];
                  return (
                    <div className="d-flex justify-content-between align-items-start mb-2 small" key={ed}>
                      <span>{editionLabel(ed)}</span>
                      {quote?.originalAmountFormatted ? (
                        <PromoPrice
                          original={quote.originalAmountFormatted}
                          current={quote.amountFormatted}
                        />
                      ) : (
                        <strong>
                          {quote?.amountFormatted ??
                            (ed === "enterprise" || ed.includes("enterprise")
                              ? "Sur devis"
                              : "—")}
                        </strong>
                      )}
                    </div>
                  );
                })}
                <p className="small text-secondary mb-3">USD/mois — tarif indicatif, validation commerciale.</p>
                  </>
                ) : (
                  <p className="small text-secondary mb-3">
                    {locale === "en"
                      ? "Pricing on request — contact ITaaS RDC for a scoped proposal."
                      : "Tarification sur demande — contactez ITaaS RDC pour une proposition adaptée."}
                  </p>
                )}

                {showPricing ? (
                <Link
                  className="btn btn-primary w-100 mb-2"
                  to={buildConfigureHrefForProduct(product)}
                >
                  {commercialCtaLabelForProduct(product, locale)}
                </Link>
                ) : null}
                {showTrial ? (
                  <Link
                    className="btn btn-outline-primary w-100 mb-2"
                    to={buildConfigureHrefForProduct(product)}
                  >
                    {commercialCtaLabelForProduct(product, locale)}
                  </Link>
                ) : null}
                {showDemo ? (
                  <Link
                    className={`btn ${showPricing || showTrial ? "btn-outline-primary" : "btn-primary"} w-100 mb-2`}
                    to={`/contact?product=${product.slug}&type=demo`}
                  >
                    Demander une démo
                  </Link>
                ) : !showTrial && ctaAllowed(product, "CONTACT") ? (
                  <Link
                    className={`btn ${showPricing ? "btn-outline-secondary" : "btn-primary"} w-100 mb-2`}
                    to={`/contact?product=${product.slug}&type=info`}
                  >
                    Contacter ITaaS RDC
                  </Link>
                ) : null}
                {showQuote ? (
                <Link
                  className="btn btn-outline-secondary w-100 mb-2"
                  to={buildConfigureHrefForProduct(product)}
                >
                  {commercialCtaLabelForProduct(product, locale)}
                </Link>
                ) : null}
                {product.slug === "edu" && (
                  <Link
                    className="btn btn-outline-success w-100 mb-2"
                    to="/affiliation/inscription?product=edu"
                  >
                    Devenir ambassadeur Edu — gagner en recommandant
                  </Link>
                )}
                {showPricing ? (
                <Link className="btn btn-link w-100" to="/pricing">
                  Voir toutes les grilles →
                </Link>
                ) : null}
                  </>
                )}
              </div>
            </div>

            {!prelaunch && (
              <div className="card border-0 shadow-sm mt-3">
                <div className="card-body small">
                  <h3 className="h6 fw-bold">Déjà client ?</h3>
                  <a className="btn btn-sm btn-outline-secondary w-100" href={SUITE_URL}>
                    Accéder à la Suite ITaaS
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {product.complementary && product.complementary.length > 0 && (
          <section className="mt-5 pt-5 border-top">
            <h2 className="h4 fw-bold mb-4">Produits complémentaires</h2>
            <div className="row g-4">
              {product.complementary.map((p) => (
                <div className="col-md-4" key={p.slug}>
                  <ProductCardView product={p} />
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-5 pt-4 border-top">
          <PublisherTrustStrip compact />
        </section>
      </div>
    </SiteLayout>
  );
}
